import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';
import ThemeToggle from '../ThemeToggle';
import ParentsTab from './ParentsTab';
import ClassesTab from './ClassesTab';
import AuditLogsTab from './AuditLogsTab';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('parents'); // 'parents', 'classes', 'audit-logs'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const { logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login');
      return;
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeTab]);

  const openPasswordModal = () => {
    setPasswordError('');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordLoading(false);
    setPasswordError('');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitPasswordChange = async (event) => {
    event.preventDefault();
    setPasswordError('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Please fill in all password fields.');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New Password and Confirm New Password must match');
      return;
    }

    try {
      setPasswordLoading(true);
      await api.put('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      closePasswordModal();
      window.alert('Password updated successfully.');
    } catch (err) {
      setPasswordError(handleApiError(err));
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="dashboard-container admin-dashboard-view">
      <div className="admin-navbar">
        <div className="admin-navbar-content">
          <h1>Admin Panel</h1>
          <div className="admin-navbar-tabs">
            <button
              className={`admin-navbar-tab ${activeTab === 'parents' ? 'active' : ''}`}
              onClick={() => setActiveTab('parents')}
            >
              Parents & Children
            </button>
            <button
              className={`admin-navbar-tab ${activeTab === 'classes' ? 'active' : ''}`}
              onClick={() => setActiveTab('classes')}
            >
              School Classes
            </button>
            <button
              className={`admin-navbar-tab ${activeTab === 'audit-logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('audit-logs')}
            >
              Audit Logs
            </button>
          </div>
          <div className="admin-navbar-actions">
            <button className="btn-secondary" onClick={openPasswordModal}>
              Change Password
            </button>
            <ThemeToggle />
            <button className="btn-logout" onClick={logout}>
              Logout
            </button>
          </div>
          <button
            className="admin-navbar-hamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      <div
        className={`admin-mobile-menu-backdrop ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`admin-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="admin-mobile-menu-header">
          <h2>Menu</h2>
          <button
            className="admin-mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>
        <div className="admin-mobile-menu-tabs">
          <button
            className={`admin-mobile-menu-tab ${activeTab === 'parents' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('parents');
              setMobileMenuOpen(false);
            }}
          >
            Parents & Children
          </button>
          <button
            className={`admin-mobile-menu-tab ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('classes');
              setMobileMenuOpen(false);
            }}
          >
            School Classes
          </button>
          <button
            className={`admin-mobile-menu-tab ${activeTab === 'audit-logs' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('audit-logs');
              setMobileMenuOpen(false);
            }}
          >
            Audit Logs
          </button>
        </div>
        <div className="admin-mobile-menu-actions">
          <button
            className="btn-secondary"
            onClick={() => {
              openPasswordModal();
              setMobileMenuOpen(false);
            }}
          >
            Change Password
          </button>
          <ThemeToggle />
          <button
            className="btn-logout"
            onClick={() => {
              logout();
              setMobileMenuOpen(false);
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Parents Tab */}
      {activeTab === 'parents' && <ParentsTab />}

      {/* Classes Tab */}
      {activeTab === 'classes' && <ClassesTab />}

      {/* Audit Logs Tab */}
      {activeTab === 'audit-logs' && <AuditLogsTab />}

      {showPasswordModal && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="modal-close-btn" onClick={closePasswordModal} aria-label="Close modal">
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={submitPasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {passwordError && (
                  <div className="error-message" style={{ marginBottom: 0 }}>
                    {passwordError}
                  </div>
                )}
                <div className="form-group">
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                    minLength={1}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password"
                    minLength={6}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Repeat new password"
                    minLength={6}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                  {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <div style={{ marginTop: '0.25rem', color: '#dc2626', fontSize: '0.8rem' }}>
                      Passwords do not match
                    </div>
                  )}
                  {passwordForm.newPassword &&
                    passwordForm.confirmPassword &&
                    passwordForm.newPassword === passwordForm.confirmPassword && (
                      <div style={{ marginTop: '0.25rem', color: '#16a34a', fontSize: '0.8rem', fontWeight: 600 }}>
                        ✓ Passwords match
                      </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button type="button" className="btn-secondary" onClick={closePasswordModal} style={{ flex: 1 }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn" disabled={passwordLoading} style={{ flex: 1 }}>
                    {passwordLoading ? 'Saving...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
